import Vue from 'vue'
import { getIndustryIds } from '~/utils/industries'
import { serializeLocationPayload } from '~/utils/location'
import { normalizeSalaryCurrency } from '~/utils/salary'
import { captureRecruitmentContext } from '~/utils/recruitmentContext'

const isAttachment = media =>
	(media.collection || media.collection_name) === 'attachment'

export const state = () => ({
	applicantProfile: null,
	latestAttachmentsRequestId: 0
})

export const getters = {
	applicantProfile(state) {
		return state.applicantProfile
	},
	attachments: state => applicantProfileId =>
		state.applicantProfile &&
		String(state.applicantProfile.id) === String(applicantProfileId)
			? (state.applicantProfile.media || []).filter(isAttachment)
			: []
}

const hasLocationFields = applicantProfile =>
	[
		'location_type',
		'outside_location',
		'provinces',
		'province',
		'province_id'
	].some(field =>
		Object.prototype.hasOwnProperty.call(applicantProfile || {}, field)
	)

export const actions = {
	async fetchAttachments({ commit, state, rootState }, applicantProfileId) {
		const profile = state.applicantProfile
		if (!profile || String(profile.id) !== String(applicantProfileId))
			return false

		const isCurrentContext = captureRecruitmentContext(rootState)
		commit('invalidateAttachmentsRequest')
		const request = state.latestAttachmentsRequestId
		const isCurrent = () =>
			request === state.latestAttachmentsRequestId &&
			profile === state.applicantProfile &&
			isCurrentContext()
		let response
		try {
			response = await this.$axios.$get(
				`/applicant-profile/${applicantProfileId}/attachments`
			)
		} catch (error) {
			if (!isCurrent()) return false
			throw error
		}
		if (!isCurrent()) return false
		commit('setAttachments', {
			applicantProfileId,
			attachments: Array.isArray(response?.attachments)
				? response.attachments
				: []
		})
		return true
	},

	async deleteAttachment(
		{ commit, state, getters, rootState },
		{ applicantProfileId, attachmentId }
	) {
		const profile = state.applicantProfile
		if (!profile || String(profile.id) !== String(applicantProfileId))
			return false

		const isCurrentContext = captureRecruitmentContext(rootState)
		await this.$axios.$delete(
			`/applicant-profile/${applicantProfileId}/attachment/${attachmentId}`
		)
		if (profile !== state.applicantProfile || !isCurrentContext())
			return false

		// An in-flight read from before deletion must not restore the removed file.
		commit('invalidateAttachmentsRequest')
		commit('setAttachments', {
			applicantProfileId,
			attachments: getters
				.attachments(applicantProfileId)
				.filter(media => String(media.id) !== String(attachmentId))
		})
		return true
	},

	async fetch({ commit, state }) {
		await this.$axios
			.$get(`/applicant-profile`)
			.then(data => {
				commit('setApplicantProfile', data)
			})
			.catch(e => {
				console.warn(
					'Failed to fetch applicant profile',
					e?.response?.status,
					e?.response?.data
				)

				commit('setApplicantProfile', null)
			})

		return state.applicantProfile
	},

	async needApplicantProfile({ state, dispatch }, forceRefresh = false) {
		if (!state.applicantProfile || forceRefresh) {
			await dispatch('fetch')
		}

		return state.applicantProfile
	},

	async store({ commit }, payload) {
		// Extract item and context from payload
		const item = payload.applicantProfile || payload
		const context = payload.context || {}

		// Set query parameter if this is a criteria update
		const url = context.isCriteriaUpdate
			? `/applicant-profile?from_criteria=true`
			: `/applicant-profile`

		return await this.$axios.$post(url, item).then(data => {
			commit('setApplicantProfile', data)
			return data
		})
	},

	async update({ dispatch, commit }, payload) {
		// Extract applicantProfile and context from payload
		const applicantProfile = payload.applicantProfile || payload
		const context = payload.context || {}

		let languages

		if (applicantProfile.languages) {
			languages = applicantProfile.languages.map(language => {
				// The language.vue component normalizes pivot.niveau onto
				// language.niveau on mount and binds the dropdown via
				// v-model="language.niveau", so the latest user-edited value
				// always lives at the top level. pivot.niveau is the stale
				// imported value and is only used as a fallback.
				return {
					id: language.id,
					attributes: {
						niveau: language.niveau != null
							? language.niveau
							: (language.pivot ? language.pivot.niveau : null),
						data_source: language.pivot
							? language.pivot.data_source
							: null
					}
				}
			})
		}

		let educationTypes = {}

		if (applicantProfile.educationTypes) {
			educationTypes = applicantProfile.educationTypes.map(
				educationType => {
					return {
						id: educationType.id,
						attributes: {
							name: educationType.name != null
								? educationType.name
								: (educationType.pivot ? educationType.pivot.name : null),
							end_year: educationType.end_year != null
								? educationType.end_year
								: (educationType.pivot ? educationType.pivot.end_year : null),
							data_source: educationType.pivot
								? educationType.pivot.data_source
								: null,
							row_key: educationType.pivot
								? educationType.pivot.row_key
								: null
						}
					}
				}
			)
		}

		let provinces = {}

		if (applicantProfile.provinces) {
			provinces = applicantProfile.provinces.map(province => {
				return {
					id: province.id,
					attributes: {}
				}
			})
		}

		const industryIds = getIndustryIds(applicantProfile, {
			preserveExplicitEmpty:
				context.isCriteriaUpdate &&
				Array.isArray(applicantProfile.industry_ids) &&
				applicantProfile.industry_ids.length === 0
		})

		// Force job position data to be properly formatted
		let jobPositionData = null
		if (applicantProfile.job_position_id) {
			// If we have a job position ID, create a proper job position object
			jobPositionData = {
				id: applicantProfile.job_position_id
			}

			// Add name and name_en if available from jobPosition object (from resource)
			if (
				applicantProfile.jobPosition &&
				typeof applicantProfile.jobPosition === 'object'
			) {
				if (applicantProfile.jobPosition.name) {
					jobPositionData.name = applicantProfile.jobPosition.name
				}
				if (applicantProfile.jobPosition.name_en) {
					jobPositionData.name_en =
						applicantProfile.jobPosition.name_en
				}
			}
		}
		const locationPayload = hasLocationFields(applicantProfile)
			? serializeLocationPayload(applicantProfile)
			: { provinces }

		// Log the job position data for debugging
		// console.log('Job position data before sending (fixed):', {
		//     id: applicantProfile.job_position_id,
		//     jobPositionData: jobPositionData
		// });

		let data = {
			id: applicantProfile.id,
			address: applicantProfile.address,
			address_line_2: applicantProfile.address_line_2,
			city: applicantProfile.city,
			zip: applicantProfile.zip,
			country: applicantProfile.country,
			url: applicantProfile.url,
			gender: applicantProfile.gender,
			salary: applicantProfile.salary,
			salary_currency: normalizeSalaryCurrency(
				applicantProfile.salary_currency
			),
			nationality: applicantProfile.nationality,
			birthday: applicantProfile.birthday,
			pensum: applicantProfile.pensum,
			active: applicantProfile.active,
			available_from: applicantProfile.available_from,
			start_date: applicantProfile.start_date,
			linkedin_url: applicantProfile.linkedin_url,
			linkedin_data_consent: applicantProfile.linkedin_data_consent,
			personality_test_consent: applicantProfile.personality_test_consent,
			talent_pool_opt_in: applicantProfile.talent_pool_opt_in,
			provinces: locationPayload.provinces,
			province_id: locationPayload.province_id,
			location_type: locationPayload.location_type,
			outside_location: locationPayload.outside_location,
			languages: languages,
			education_types: educationTypes,
			employment_type_id: applicantProfile.employment_type_id,
			industry_ids: industryIds,
			industry_id: industryIds[0] || null,
			occupational_field_id: applicantProfile.occupational_field_id,
			context: context // Pass context to backend
		}

		// Always include job_position_id if it exists
		if (applicantProfile.job_position_id) {
			data.job_position_id = applicantProfile.job_position_id
		}

		// Always include job_position if we have job position data
		if (jobPositionData) {
			data.job_position = jobPositionData
		}

		// Set query parameter if this is a criteria update
		const url = context.isCriteriaUpdate
			? `/applicant-profile/${data.id}?from_criteria=true`
			: `/applicant-profile/${data.id}`

		return this.$axios.$patch(url, data).then(data => {
			commit('setApplicantProfile', data)
			if (!context.skipToast) {
				dispatch(
					'toast/push',
					{
						title: data.name || this.app.i18n.t('crud.updated'),
						message: this.app.i18n.t('crud.updated-text'),
						type: 'success'
					},
					{ root: true }
				)
			}

			// Return the server response so callers can use the persisted
			// profile (with any enrichment-imported education/languages)
			// directly, rather than depending on store-getter timing.
			return data
		})
	},

	async submitAssessmentResult(
		{ commit },
		{ applicantProfileId, roundData }
	) {
		const updated = await this.$axios.$post(
			`/applicant-profile/${applicantProfileId}/assessment-result`,
			{ personality_round_data: roundData },
			{ __skipGlobalErrorToast: true }
		)

		commit('setApplicantProfile', updated)
		return updated
	},

	async updateConsent({ commit }, payload) {
		const data = await this.$axios.$patch(`/applicant-profile/${payload.id}`, {
			id: payload.id,
			personality_test_consent: payload.personality_test_consent
		})

		commit('setApplicantProfile', data)
		return data
	}
}

export const mutations = {
	setApplicantProfile(state, data) {
		state.applicantProfile = data
		state.latestAttachmentsRequestId++
	},
	invalidateAttachmentsRequest(state) {
		state.latestAttachmentsRequestId++
	},
	setAttachments(state, { applicantProfileId, attachments }) {
		if (
			!state.applicantProfile ||
			String(state.applicantProfile.id) !== String(applicantProfileId)
		)
			return
		const otherMedia = (state.applicantProfile.media || []).filter(
			media => !isAttachment(media)
		)
		Vue.set(state.applicantProfile, 'media', [
			...otherMedia,
			...attachments
		])
		Vue.set(
			state.applicantProfile,
			'has_attachments',
			attachments.length > 0
		)
	}
}
